import {Injectable, Renderer2, RendererFactory2, Inject, Optional} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {Subject} from "rxjs";
import {scriptLinks} from "../../assets/data/script-links";

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private renderer: Renderer2;
  private scriptCache: Map<string, boolean> = new Map();
  private _scriptElement: HTMLScriptElement;
  private execTimeout: number;
  private execTime = 750;
  private execScript: HTMLScriptElement;

  public isScriptLoaded$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private rendererFactory: RendererFactory2,
    @Optional() @Inject(DOCUMENT) private document: any) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public loadScript(canLoadStaticScripts: boolean, src?: string, type?: string, location: 'head' | 'body' = 'body', defer?: boolean, async?: boolean, isAnalytics: boolean = false, isFBPixel: boolean = false, isGTM: boolean = false, id?: string): void {
    if (!canLoadStaticScripts) {
      const cacheKey = isFBPixel ? `fbpixel:${id}` : isGTM ? `gtm:${id}` : src;
      if (!this.scriptCache.has(cacheKey)) {
        this._loadScript(src, type, location, defer, async, isAnalytics, isFBPixel, isGTM, id);
      }
    } else {
      for (const el of scriptLinks) {
        if (!this.scriptCache.has(el.src)) {
          this._loadScript(el.src, el.scriptType, el.location, el.defer, el.async);
        }
      }
    }
  }

  public loadWtgApplicationScript(scriptSrc: string, scriptType: string, defer: boolean, async: boolean): void {
    const script = this.renderer.createElement('script');
    script.src = scriptSrc;
    script.type = scriptType;
    script.id = "zuc-script";
    script.defer = defer;
    script.async = async;
    const target = this.document.body;
    this._scriptElement = script;
    this.renderer.appendChild(target, this._scriptElement);

    this.execTimeout = setTimeout(() => {
      this.execScript = this.renderer.createElement('script');
      this.execScript.type = scriptType;
      this.execScript.innerHTML = `
        (() => {
          createRegistrationIFrame()
        })();
      `;
      this.renderer.appendChild(target, this.execScript);
      this.isScriptLoaded$.next(false);
    }, this.execTime);
  }

  public removeScript() {
    if (this._scriptElement && this._scriptElement.parentNode) {
      this._scriptElement.parentNode.removeChild(this._scriptElement);
      this.execScript.parentNode.removeChild(this.execScript);
      this._scriptElement = null;
      this.execScript = null;
      clearTimeout(this.execTimeout);
    }
  }

  private _loadScript(scriptSrc: string, scriptType: string, location: 'head' | 'body', defer: boolean, async: boolean, isAnalytics: boolean = false, isFBPixel: boolean = false, isGTM: boolean = false, id?: string): void {
    if (isFBPixel && id) {
      this.addFBScript(id);
      this.scriptCache.set(`fbpixel:${id}`, true);
      return;
    }
    if (isGTM && id) {
      this.addGTMScript(id);
      this.scriptCache.set(`gtm:${id}`, true);
      return;
    }

    const script = this.renderer.createElement('script');
    script.src = scriptSrc;
    script.type = scriptType;
    script.defer = defer;
    script.async = async;
    script.onload = () => {
      this.scriptCache.set(scriptSrc, true);
    };
    const target = location === 'head' ? this.document.head : this.document.body;

    this.renderer.appendChild(target, script);
    if (isAnalytics) {
      this.addGtagScript(this.extractGtagId(scriptSrc));
    }
  }

  private extractGtagId(scriptSrc: string): string {
    const url = new URL(scriptSrc);
    return url.searchParams.get('id');
  }

  private addGtagScript(gaId: string) {
    const script = this.renderer.createElement('script');

    const scriptContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `;

    this.renderer.setProperty(script, 'textContent', scriptContent);
    this.renderer.appendChild(document.head, script);
  }

  private addFBScript(pixelId: string) {
    const script = this.renderer.createElement('script');

    const scriptContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;

    this.renderer.setProperty(script, 'textContent', scriptContent);
    this.renderer.appendChild(document.head, script);
  }

  private addGTMScript(gtmId: string) {
    const script = this.renderer.createElement('script');

    const scriptContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;

    this.renderer.setProperty(script, 'textContent', scriptContent);
    this.renderer.appendChild(document.head, script);
  }

}
